import { Entry, EntryCollection } from 'contentful';
import { createContext, DependencyList, EffectCallback, useContext, useEffect, useState } from 'react';
import { getContentfulClient } from './contentfulClient';

const ContentfulClientContext = createContext(getContentfulClient());
ContentfulClientContext.displayName = 'ContentfulClient';

export const useContentfulClient = () => useContext(ContentfulClientContext);

export enum ContentStatus {
  loading = 'loading',
  error = 'error',
  loaded = 'loaded',
}

export type LoadingContentState = {
  status: ContentStatus.loading;
};

export type ErrorContentState = {
  status: ContentStatus.error;
  error: any;
};

export type LoadedContentState<T> = {
  status: ContentStatus.loaded;
  data: T;
};

export type ContentState<T> = LoadingContentState | ErrorContentState | LoadedContentState<T>;

const loadingState: LoadingContentState = { status: ContentStatus.loading };

export const useContentEntry = <T>(entryId: string | undefined): ContentState<Entry<T>> => {
  const client = useContentfulClient();
  const [content, setContent] = useState<ContentState<Entry<T>>>(loadingState);
  useEffect(() => {
    setContent(loadingState);
    if (!entryId) {
      return;
    }
    (async () => {
      try {
        const data = await client.getEntry<T>(entryId, { include: 10 });
        setContent({ status: ContentStatus.loaded, data });
      } catch (error) {
        setContent({ status: ContentStatus.error, error });
        console.error('failed fetching content from contentful', error);
      }
    })();
  }, [entryId]);

  return content;
};

export const useContentEntries = <T>(query: any, deps: DependencyList = []): ContentState<EntryCollection<T>> => {
  const client = useContentfulClient();
  const [content, setContent] = useState<ContentState<EntryCollection<T>>>(loadingState);
  useEffect(() => {
    setContent(loadingState);
    (async () => {
      try {
        const data = await client.getEntries<T>({ include: 10, ...query });
        setContent({ status: ContentStatus.loaded, data });
      } catch (error) {
        setContent({ status: ContentStatus.error, error });
        console.error('failed fetching content from contentful', error);
      }
    })();
  }, deps);

  return content;
};



// WEBPACK FOOTER //
// ./packages/platform-core/content/ContentfulProvider.ts